import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../services/api.ts';

interface MediaItem {
  id: string;
  name: string;
  url: string;
  type: 'video' | 'audio';
  duration?: number;
}

interface TimelineItem {
  id: string;
  mediaItem: MediaItem;
  startTime: number;
  duration: number;
  track: 'video' | 'audio';
  isDragging?: boolean;
  isResizing?: boolean;
  trimStart?: number; // seconds offset into the source media
}

const VideoEditor: React.FC = () => {
  const navigate = useNavigate();
  const [mediaLibrary, setMediaLibrary] = useState<MediaItem[]>([]);
  const [timeline, setTimeline] = useState<TimelineItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);
  const [isPreviewPlaying, setIsPreviewPlaying] = useState(false);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);

  // Fetch media library from API
  useEffect(() => {
    const fetchMediaLibrary = async () => {
      try {
        setIsLoading(true);
        const [videosResponse, scenesResponse] = await Promise.all([
          apiRequest('/api/frame-to-video-generations'),
          apiRequest('/api/dynamic-scene-generations')
        ]);
        const videos = videosResponse.data || [];
        const scenes = scenesResponse.data || [];
        const mediaItems: MediaItem[] = [];
        videos.forEach((video: any) => {
          if (video.video_url) {
            mediaItems.push({
              id: `video-${video.id}`,
              name: video.name || `Video ${video.id}`,
              url: video.video_url,
              type: 'video',
              duration: 5
            });
          }
        });
        scenes.forEach((scene: any) => {
          if (scene.edited_url) {
            mediaItems.push({
              id: `scene-${scene.id}`,
              name: scene.name || `Scene ${scene.id}`,
              url: scene.edited_url,
              type: 'video',
              duration: 3
            });
          }
        });
        setMediaLibrary(mediaItems);
      } catch (e) {
        console.error('Failed to fetch media library:', e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchMediaLibrary();
  }, []);

  const handleDragStart = (e: React.DragEvent, mediaItem: MediaItem) => {
    e.dataTransfer.setData('application/json', JSON.stringify(mediaItem));
  };

  const handleDrop = (e: React.DragEvent, track: 'video' | 'audio') => {
    e.preventDefault();
    const data = e.dataTransfer.getData('application/json');
    if (!data) return;
    const mediaItem: MediaItem = JSON.parse(data);
    const trackItems = timeline.filter(item => item.track === track);
    const startTime = trackItems.length > 0 
      ? Math.max(...trackItems.map(item => item.startTime + item.duration))
      : 0;
    const newTimelineItem: TimelineItem = {
      id: `${track}-${Date.now()}`,
      mediaItem,
      startTime,
      duration: Math.min(mediaItem.duration || 5, mediaItem.duration || 5),
      track
    };
    setTimeline(normalizeVideoTrack([...timeline, newTimelineItem]));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const removeFromTimeline = (id: string) => {
    setTimeline(prev => normalizeVideoTrack(prev.filter(item => item.id !== id)));
  };

  // Keyboard delete for selected clip (Delete / Backspace)
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedClipId) {
        e.preventDefault();
        removeFromTimeline(selectedClipId);
        setSelectedClipId(null);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [selectedClipId]);

  // Get selected clip for preview
  const getSelectedClip = () => {
    if (!selectedClipId) return null;
    return timeline.find(item => item.id === selectedClipId);
  };

  // Handle preview play/pause
  const handlePreviewPlay = () => {
    if (previewVideoRef.current) {
      if (isPreviewPlaying) {
        previewVideoRef.current.pause();
        setIsPreviewPlaying(false);
      } else {
        previewVideoRef.current.play();
        setIsPreviewPlaying(true);
      }
    }
  };

  // Reset preview when selection changes
  useEffect(() => {
    setIsPreviewPlaying(false);
    if (previewVideoRef.current) {
      previewVideoRef.current.pause();
      previewVideoRef.current.currentTime = 0;
    }
  }, [selectedClipId]);


  const updateTimelineItem = (id: string, updates: Partial<TimelineItem>) => {
    setTimeline(prev => normalizeVideoTrack(prev.map(item => 
      item.id === id ? { ...item, ...updates } : item
    )));
  };

  const handleClipDrag = (e: React.MouseEvent, itemId: string) => {
    e.preventDefault();
    const startX = e.clientX;
    const startTime = timeline.find(item => item.id === itemId)?.startTime || 0;
    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = e.clientX - startX;
      const timelineWidth = 400;
      const timePerPixel = getDisplayRangeSeconds() / timelineWidth;
      const deltaTime = deltaX * timePerPixel;
      const newStartTime = Math.max(0, startTime + deltaTime);
      updateTimelineItem(itemId, { startTime: newStartTime });
    };
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // DISABLED: Trimming functionality
  // const handleClipResize = (e: React.MouseEvent, itemId: string, edge: 'left' | 'right') => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   const startX = e.clientX;
  //   const item = timeline.find(item => item.id === itemId);
  //   if (!item) return;
  //   const originalDuration = item.mediaItem.duration || 10;
  //   const originalTrimStart = item.trimStart || 0;
    
  //   const handleMouseMove = (e: MouseEvent) => {
  //     const deltaX = e.clientX - startX;
  //     const timelineWidth = 400;
  //     const timePerPixel = getDisplayRangeSeconds() / timelineWidth;
  //     const deltaTime = deltaX * timePerPixel;
      
  //     if (edge === 'left') {
  //       const newStartTime = Math.max(0, item.startTime + deltaTime);
  //       const newTrimStart = Math.max(0, Math.min(originalDuration, originalTrimStart + deltaTime));
  //       const remaining = Math.max(0.1, originalDuration - newTrimStart);
  //       const newDuration = Math.max(0.1, Math.min(remaining, item.duration - deltaTime));
  //       updateTimelineItem(itemId, { 
  //         startTime: newStartTime,
  //         trimStart: newTrimStart,
  //         duration: newDuration
  //       });
  //     } else {
  //       const maxDur = Math.max(0.1, originalDuration - originalTrimStart);
  //       const newDuration = Math.max(0.1, Math.min(maxDur, item.duration + deltaTime));
  //       updateTimelineItem(itemId, { duration: newDuration });
  //     }
  //   };
    
  //   const handleMouseUp = () => {
  //     document.removeEventListener('mousemove', handleMouseMove);
  //     document.removeEventListener('mouseup', handleMouseUp);
  //   };
    
  //   document.addEventListener('mousemove', handleMouseMove);
  //   document.addEventListener('mouseup', handleMouseUp);
  // };

  const exportTimeline = async () => {
    if (timeline.length === 0) {
      alert('Please add some media to the timeline first!');
      return;
    }
    try {
      setIsExporting(true);
      const token = localStorage.getItem('auth_token');
      if (!token) throw new Error('No authentication token found');
      const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/$/, '');
      const response = await fetch(`${API_BASE_URL}/api/timeline-export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          timeline: timeline.map(item => ({
            url: item.mediaItem.url,
            startTime: item.startTime,
            duration: item.duration,
            track: item.track
            // DISABLED: trimStart: item.trimStart || 0
          }))
        })
      });
      if (!response.ok) throw new Error(`Export failed: ${response.statusText}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'exported_video.mp4';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      alert('Video exported and downloaded successfully!');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const getTotalDuration = () => {
    if (timeline.length === 0) return 0;
    return Math.max(...timeline.map(item => item.startTime + item.duration));
  };

  const getVideoDuration = () => {
    const videoItems = timeline.filter(item => item.track === 'video');
    if (videoItems.length === 0) return 0;
    return Math.max(...videoItems.map(item => item.startTime + item.duration));
  };

  const getDisplayRangeSeconds = () => {
    const total = getTotalDuration();
    if (total <= 60) return 60;
    const over = total - 60;
    const extra = Math.ceil(over / 30) * 30;
    return 60 + extra;
  };

  const normalizeVideoTrack = (items: TimelineItem[]): TimelineItem[] => {
    const videos = items
      .filter(i => i.track === 'video')
      .sort((a, b) => a.startTime - b.startTime);
    let cursor = 0;
    const idToStart = new Map<string, number>();
    videos.forEach(v => {
      idToStart.set(v.id, cursor);
      cursor += Math.max(0, v.duration);
    });
    return items.map(item =>
      item.track === 'video' && idToStart.has(item.id)
        ? { ...item, startTime: idToStart.get(item.id)! }
        : item
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading media library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="px-4 py-3">
          <div className="flex justify-between items-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors duration-200"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Back to Home</span>
            </button>
            <h1 className="text-xl font-bold text-gray-900">Video Editor</h1>
            <button
              onClick={exportTimeline}
              disabled={isExporting || timeline.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {isExporting ? 'Exporting...' : 'Export Video'}
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        {/* Main Content Area */}
        <div className="flex-1 flex">
          {/* Media Library Sidebar */}
          <div className="w-64 bg-white border-r border-gray-200 p-4">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Media Library</h2>
            <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
              {mediaLibrary.map((item) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item)}
                  className="p-3 border border-gray-200 rounded-lg cursor-move hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">
                      {item.type === 'video' ? '🎬' : '🎵'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.type} • {item.duration}s
                      </p>
                    </div>
                  </div>
                </div>
              ))}
              {mediaLibrary.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">
                  No media found. Create some videos or scenes first!
                </p>
              )}
            </div>
          </div>

          {/* Preview Area - Centered with logical aspect ratio */}
          <div className="flex-1 bg-gray-50 p-4 flex items-center justify-center">
            <div className="bg-white rounded-lg shadow-sm border p-4 w-full max-w-2xl">
              <div className="bg-black rounded-lg aspect-video flex items-center justify-center relative overflow-hidden">
                {getSelectedClip() ? (
                  <video
                    ref={previewVideoRef}
                    src={getSelectedClip()?.mediaItem.url}
                    className="h-full w-full object-contain"
                    controls
                    onPlay={() => setIsPreviewPlaying(true)}
                    onPause={() => setIsPreviewPlaying(false)}
                    onEnded={() => setIsPreviewPlaying(false)}
                  />
                ) : (
                  <p className="text-gray-400">Select a clip to preview</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Timeline at Bottom - Full Width */}
        <div className="bg-white border-t border-gray-200 p-4">
          <div className="max-w-full">
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Timeline</h2>
              
              {/* Timeline Container */}
              <div className="bg-gray-100 rounded-lg p-4">
                {/* Time Ruler */}
                <div className="mb-2">
                  <div className="h-1 bg-gray-300 rounded-full relative">
                    {(() => {
                      const range = getDisplayRangeSeconds();
                      const interval = 30; // Always use 30-second intervals
                      const markers: React.ReactNode[] = [];
                      for (let t = 0; t <= range; t += interval) {
                        const pos = (t / range) * 100;
                        markers.push(
                          <div key={`tick-${t}`} className="absolute top-0" style={{ left: `${pos}%` }}>
                            <div className="w-px h-full bg-gray-400" />
                            <div className="absolute -top-4 left-0 text-xs text-gray-500 whitespace-nowrap">{t}s</div>
                          </div>
                        );
                      }
                      return markers;
                    })()}

                    {getVideoDuration() > 0 && (
                      <div 
                        className="absolute -top-4 text-xs text-red-500 font-semibold whitespace-nowrap"
                        style={{ left: `${(getVideoDuration() / getDisplayRangeSeconds()) * 100}%` }}
                      >
                        {getVideoDuration().toFixed(1)}s
                      </div>
                    )}
                  </div>
                </div>

                {/* Video Track */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 flex items-center">
                      <span className="w-3 h-3 bg-blue-500 rounded mr-2"></span>
                      Video
                    </span>
                  </div>
                  <div
                    className="h-12 border-2 border-dashed border-gray-300 rounded bg-gray-50 relative overflow-hidden"
                    onDrop={(e) => handleDrop(e, 'video')}
                    onDragOver={handleDragOver}
                    onMouseDown={() => setSelectedClipId(null)}
                  >
                    {timeline
                      .filter(item => item.track === 'video')
                      .map((item) => (
                        <div
                          key={item.id}
                          className={`absolute top-0 bg-blue-500 text-white text-xs p-2 rounded cursor-move hover:bg-blue-600 transition-colors flex items-center justify-center min-w-[80px] h-10 group ${selectedClipId === item.id ? 'ring-2 ring-offset-2 ring-blue-400' : ''}`}
                          style={{
                            left: `${(item.startTime / Math.max(getDisplayRangeSeconds(), 1)) * 100}%`,
                            width: `${(item.duration / Math.max(getDisplayRangeSeconds(), 1)) * 100}%`,
                            minWidth: '80px'
                          }}
                          onClick={(e) => { e.stopPropagation(); setSelectedClipId(item.id); }}
                          onMouseDown={(e) => handleClipDrag(e, item.id)}
                        >
                          {/* DISABLED: Left resize handle */}
                          {/* <div
                            className="absolute left-0 top-0 w-2 h-full bg-blue-300 hover:bg-blue-400 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity"
                            onMouseDown={(e) => handleClipResize(e, item.id, 'left')}
                          /> */}
                          {/* DISABLED: Right resize handle */}
                          {/* <div
                            className="absolute right-0 top-0 w-2 h-full bg-blue-300 hover:bg-blue-400 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity"
                            onMouseDown={(e) => handleClipResize(e, item.id, 'right')}
                          /> */}
                          {/* Clip content */}
                          <span className="truncate z-10 relative">{item.mediaItem.name}</span>
                          {/* Duration label */}
                          <div className="absolute -top-1 -right-1 bg-black bg-opacity-75 text-white text-xs px-1 py-0.5 rounded text-center min-w-[24px] z-15">
                            {item.duration.toFixed(1)}s
                          </div>
                        </div>
                      ))}
                    {timeline.filter(item => item.track === 'video').length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                        Drop video clips here
                      </div>
                    )}
                  </div>
                </div>

                {/* Audio Track */}
                <div className="mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 flex items-center">
                      <span className="w-3 h-3 bg-green-500 rounded mr-2"></span>
                      Audio
                    </span>
                  </div>
                  <div
                    className="h-12 border-2 border-dashed border-gray-300 rounded bg-gray-50 relative overflow-hidden"
                    onDrop={(e) => handleDrop(e, 'audio')}
                    onDragOver={handleDragOver}
                    onMouseDown={() => setSelectedClipId(null)}
                  >
                    {timeline
                      .filter(item => item.track === 'audio')
                      .map((item) => (
                        <div
                          key={item.id}
                          className={`absolute top-0 bg-green-500 text-white text-xs p-2 rounded cursor-move hover:bg-green-600 transition-colors flex items-center justify-center min-w-[80px] h-10 group ${selectedClipId === item.id ? 'ring-2 ring-offset-2 ring-green-400' : ''}`}
                          style={{
                            left: `${(item.startTime / Math.max(getDisplayRangeSeconds(), 1)) * 100}%`,
                            width: `${(item.duration / Math.max(getDisplayRangeSeconds(), 1)) * 100}%`,
                            minWidth: '80px'
                          }}
                          onClick={(e) => { e.stopPropagation(); setSelectedClipId(item.id); }}
                          onMouseDown={(e) => handleClipDrag(e, item.id)}
                        >
                          {/* DISABLED: Left resize handle */}
                          {/* <div
                            className="absolute left-0 top-0 w-2 h-full bg-green-300 hover:bg-green-400 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity"
                            onMouseDown={(e) => handleClipResize(e, item.id, 'left')}
                          /> */}
                          {/* DISABLED: Right resize handle */}
                          {/* <div
                            className="absolute right-0 top-0 w-2 h-full bg-green-300 hover:bg-green-400 cursor-ew-resize opacity-0 group-hover:opacity-100 transition-opacity"
                            onMouseDown={(e) => handleClipResize(e, item.id, 'right')}
                          /> */}
                          {/* Clip content */}
                          <span className="truncate z-10 relative">{item.mediaItem.name}</span>
                          {/* Duration label */}
                          <div className="absolute -top-1 -right-1 bg-black bg-opacity-75 text-white text-xs px-1 py-0.5 rounded text-center min-w-[24px] z-15">
                            {item.duration.toFixed(1)}s
                          </div>
                        </div>
                      ))}
                    {timeline.filter(item => item.track === 'audio').length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
                        Drop audio clips here
                      </div>
                    )}
                  </div>
                </div>

                {/* Timeline Controls */}
                <div className="flex items-center justify-between mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-4 text-sm text-gray-600">
                    <span>Duration: {getTotalDuration()}s</span>
                    <span>Clips: {timeline.length}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setTimeline([])}
                      className="px-3 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoEditor;


